import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import type { PlantSymbol } from "@garden/types/db";
import "./MapPin.css";
export type MapPinSymbol = PlantSymbol;

interface MapPinProps {
  x: number;
  y: number;
  symbol: MapPinSymbol;
  selected?: boolean;
  draggable?: boolean;
  onClick?: () => void;
  onDragMove?: (x: number, y: number) => void;
  onDragEnd?: (x: number, y: number) => void;
}

export const PIN_SIZES: Record<MapPinSymbol, { w: number; h: number }> = {
  // trees
  tree:           { w: 80, h: 80 },
  tree_conifer:   { w: 64, h: 70 },
  tree_fruit:     { w: 60, h: 60 },
  // shrubs
  shrub:          { w: 58, h: 58 },
  shrub_topiary:  { w: 40, h: 50 },
  bamboo:         { w: 32, h: 50 },
  // flowers
  rose:           { w: 22, h: 22 },
  daisy:          { w: 28, h: 28 },
  tulip:          { w: 22, h: 34 },
  sunflower:      { w: 30, h: 30 },
  lavender:       { w: 28, h: 38 },
  foxglove:       { w: 24, h: 42 },
  // climbers
  vine:           { w: 30, h: 30 },
  climber:        { w: 30, h: 30 },
  // edibles
  veg:            { w: 20, h: 20 },
  carrot:         { w: 24, h: 34 },
  tomato:         { w: 24, h: 24 },
  herb:           { w: 28, h: 24 },
  // other
  fern:           { w: 26, h: 38 },
  container:      { w: 20, h: 20 },
};

/** Convert "tree_conifer" → "Tree_conifer" for the sprite id "mpTree_conifer". */
function symbolToSpriteId(symbol: MapPinSymbol): string {
  return symbol.charAt(0).toUpperCase() + symbol.slice(1);
}

const DRAG_THRESHOLD_PX = 3;

export function isMapPinSymbol(value: unknown): value is MapPinSymbol {
  return typeof value === "string" && value in PIN_SIZES;
}

function svgPointFromEvent(event: ReactPointerEvent<SVGGElement> | PointerEvent, svg: SVGSVGElement): { x: number; y: number } | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const inv = ctm.inverse();
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const { x, y } = pt.matrixTransform(inv);
  return { x, y };
}

export function MapPin({
  x,
  y,
  symbol,
  selected,
  draggable,
  onClick,
  onDragMove,
  onDragEnd,
}: MapPinProps) {
  const { w, h } = PIN_SIZES[symbol];
  const id = symbolToSpriteId(symbol);

  const drag = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    moved: boolean;
    svg: SVGSVGElement | null;
  } | null>(null);

  const handlePointerDown = (e: ReactPointerEvent<SVGGElement>) => {
    e.stopPropagation();
    if (!draggable) return;
    const svg = e.currentTarget.ownerSVGElement;
    drag.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moved: false,
      svg,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<SVGGElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId || !d.svg) return;
    const dx = e.clientX - d.startClientX;
    const dy = e.clientY - d.startClientY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    d.moved = true;
    const pt = svgPointFromEvent(e, d.svg);
    if (pt && onDragMove) onDragMove(pt.x, pt.y);
  };

  const handlePointerUp = (e: ReactPointerEvent<SVGGElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer was already released — ignore
    }
    drag.current = null;
    if (!d.moved) {
      onClick?.();
      return;
    }
    if (!d.svg) return;
    const pt = svgPointFromEvent(e, d.svg);
    if (pt && onDragEnd) onDragEnd(pt.x, pt.y);
  };

  const handleClick = (e: ReactMouseEvent<SVGGElement>) => {
    e.stopPropagation();
    if (draggable) return; // pointerup handles click in drag-mode
    onClick?.();
  };

  return (
    <>
      {selected && (
        <circle
          cx={x}
          cy={y}
          r={(w / 2) * 1.5}
          fill="none"
          stroke="var(--terracotta, #b66b48)"
          strokeWidth="1.4"
          strokeDasharray="3 3"
          className="MapPin__pulse"
          pointerEvents="none"
        />
      )}
      <g
        onPointerDown={draggable ? handlePointerDown : undefined}
        onPointerMove={draggable ? handlePointerMove : undefined}
        onPointerUp={draggable ? handlePointerUp : undefined}
        onClick={handleClick}
        style={{
          cursor: draggable ? "grab" : onClick ? "pointer" : "default",
          touchAction: draggable ? "none" : undefined,
        }}
      >
        <use
          href={`#mp${id}`}
          x={x - w / 2}
          y={y - h / 2}
          width={w}
          height={h}
        />
      </g>
    </>
  );
}
